import { useState } from 'react'
import { FiX, FiCheck } from 'react-icons/fi'
import { useWalletClient } from 'wagmi'
import { getEthersSignerFromWalletClient } from '../services/wallet'

const DECLARATION_TEXT = `By proceeding, you declare and confirm, under your sole responsibility, that you are the lawful owner of this card.
If you are not the rightful owner, you agree to be held legally responsible for any and all civil and criminal consequences arising from such false declaration.`

/**
 * @param {'wallet' | 'email'} variant
 * @param {string | null} signingWallet - required when variant is wallet
 * @param {string | null} attestationEmail - required when variant is email (M1NT account email)
 */
export default function OwnershipDeclarationModal({
  isOpen,
  onClose,
  onAccept,
  variant = 'wallet',
  signingWallet = null,
  attestationEmail = null,
}) {
  const { data: walletClient } = useWalletClient()
  const [checked, setChecked] = useState(false)
  const [signing, setSigning] = useState(false)
  const [error, setError] = useState(null)

  if (!isOpen) return null

  const handleProceed = async () => {
    if (variant === 'email') {
      if (!checked || !attestationEmail) {
        setError('Please confirm using the checkbox to continue.')
        return
      }
      setError(null)
      setSigning(true)
      try {
        const signedAt = new Date().toISOString()
        const messageToSign = `${DECLARATION_TEXT}\n\nAttested via M1NT account email at: ${signedAt}\nEmail: ${attestationEmail}`
        onAccept({
          message: messageToSign,
          signedAt,
          method: 'email',
          email: attestationEmail,
        })
        onClose()
      } catch (err) {
        console.error('Declaration attest error:', err)
        setError(err?.message || 'Something went wrong. Please try again.')
      } finally {
        setSigning(false)
      }
      return
    }

    if (!checked || !signingWallet) {
      setError('Please connect your wallet and check the box to sign.')
      return
    }
    setError(null)
    setSigning(true)
    try {
      const signer = walletClient ? await getEthersSignerFromWalletClient(walletClient) : null
      if (!signer) {
        throw new Error('Could not get wallet signer. Please try again.')
      }
      const signedAt = new Date().toISOString()
      const messageToSign = `${DECLARATION_TEXT}\n\nSigned at: ${signedAt}`
      const signature = await signer.signMessage(messageToSign)
      onAccept({
        message: messageToSign,
        signature,
        signedAt,
        wallet: signingWallet,
        method: 'wallet',
      })
      onClose()
    } catch (err) {
      console.error('Declaration sign error:', err)
      const code = err?.code ?? err?.info?.error?.code
      const rejected =
        code === 'ACTION_REJECTED' ||
        code === 4001 ||
        /user rejected|denied the request/i.test(String(err?.message || err?.shortMessage || ''))
      setError(
        rejected
          ? 'Signature was cancelled in your wallet. Approve the request to continue, or sign in with email if you do not use a wallet for this account.'
          : err?.message || err?.shortMessage || 'Failed to sign. Please try again.'
      )
    } finally {
      setSigning(false)
    }
  }

  const handleClose = () => {
    if (!signing) {
      setChecked(false)
      setError(null)
      onClose()
    }
  }

  const isEmail = variant === 'email'

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[60] p-4 modal-overlay"
      onClick={handleClose}
    >
      <div
        className="modal-content max-w-lg w-full overflow-hidden shadow-2xl animate-fade-in rounded-2xl border border-white/10 bg-[#0A0E27]"
        style={{
          background:
            'linear-gradient(180deg, rgba(16, 24, 48, 0.95) 0%, rgba(10, 14, 39, 0.98) 100%)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-white/5">
          <h2 className="text-xl font-bold text-white">Ownership Declaration</h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={signing}
            className="p-2 hover:bg-white/5 rounded-xl transition-colors text-gray-400 hover:text-white disabled:opacity-50"
          >
            <FiX size={22} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
            {DECLARATION_TEXT}
          </p>

          {isEmail && attestationEmail && (
            <p className="text-xs text-gray-400 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
              This will be recorded for your M1NT account:{' '}
              <span className="text-cyan-300 font-medium break-all">{attestationEmail}</span>
            </p>
          )}

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                setChecked(e.target.checked)
                setError(null)
              }}
              disabled={signing}
              className="mt-1 w-4 h-4 rounded border-white/30 bg-white/5 text-cyan-500 focus:ring-cyan-500/50"
            />
            <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
              {isEmail
                ? 'I confirm this declaration under my M1NT email account (same as signing in).'
                : 'I sign this declaration using my crypto wallet'}
            </span>
          </label>

          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={signing}
              className="flex-1 py-3 px-4 rounded-xl font-medium text-gray-400 border border-white/10 hover:bg-white/5 hover:text-white transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleProceed}
              disabled={!checked || signing}
              className="flex-1 py-3 px-4 rounded-xl font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background:
                  checked && !signing
                    ? 'linear-gradient(135deg, #00D9FF 0%, #B026FF 100%)'
                    : 'rgba(255,255,255,0.1)',
                border: `1px solid ${checked && !signing ? 'rgba(180, 156, 245, 0.5)' : 'rgba(255,255,255,0.1)'}`,
              }}
            >
              {signing
                ? isEmail
                  ? 'Confirming…'
                  : 'Signing…'
                : isEmail
                  ? 'Confirm declaration'
                  : 'Proceed'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
